import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from "@sentry/react";
import { User } from '@supabase/supabase-js';

interface InactivityConfig {
  timeoutMs: number;
  warningMs: number;
  storageKey: string;
  events: string[];
}

interface InactivityState {
  isWarning: boolean;
  remainingSeconds: number;
  lastActivity: number;
}

const DEFAULT_CONFIG: InactivityConfig = {
  timeoutMs: 25 * 60 * 1000, // 25 minutes
  warningMs: 23 * 60 * 1000, // 23 minutes  
  storageKey: 'last_activity_timestamp',
  events: ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click', 'focus', 'wheel']
};

export function useInactivityTimer(
  config: Partial<InactivityConfig> = {},
  user: User | null,
  signOut: () => Promise<{ error: any }>
) {
  const fullConfig = { ...DEFAULT_CONFIG, ...config };
  // Remove the useAuth call since we now receive user and signOut as parameters
  
  const [state, setState] = useState<InactivityState>({
    isWarning: false,
    remainingSeconds: 0,
    lastActivity: Date.now()
  });

  const timerRef = useRef<NodeJS.Timeout>();
  const warningTimerRef = useRef<NodeJS.Timeout>();
  const countdownRef = useRef<NodeJS.Timeout>();
  const isActiveRef = useRef(true);

  // Update activity timestamp
  const updateActivity = useCallback(() => {
    if (!user || !isActiveRef.current) return;

    const now = Date.now();
    setState(prev => ({ ...prev, lastActivity: now, isWarning: false }));
    
    // Persist to localStorage for cross-tab sync
    try {
      localStorage.setItem(fullConfig.storageKey, now.toString());
    } catch (error) {
      // Ignore localStorage errors
    }

    // Clear existing timers
    if (timerRef.current) clearTimeout(timerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownRef.current) clearTimeout(countdownRef.current);

    // Set warning timer
    warningTimerRef.current = setTimeout(() => {
      if (!isActiveRef.current) return;
      
      setState(prev => ({
        ...prev,
        isWarning: true,
        remainingSeconds: Math.ceil((fullConfig.timeoutMs - fullConfig.warningMs) / 1000)
      }));

      // Start countdown
      let seconds = Math.ceil((fullConfig.timeoutMs - fullConfig.warningMs) / 1000);
      countdownRef.current = setInterval(() => {
        seconds--;
        if (seconds <= 0) {
          clearInterval(countdownRef.current);
          performAutoLogout();
        } else {
          setState(prev => ({ ...prev, remainingSeconds: seconds }));
        }
      }, 1000);
    }, fullConfig.warningMs);

    // Set auto logout timer
    timerRef.current = setTimeout(() => {
      if (isActiveRef.current) {
        performAutoLogout();
      }
    }, fullConfig.timeoutMs);
  }, [user, fullConfig]);

  // Perform automatic logout
  const performAutoLogout = useCallback(async () => {
    if (!user) return;
    
    try {
      await signOut();
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'auth_error', component: 'useInactivityTimer', operation: 'auto_logout' },
        extra: { message: 'Auto logout failed', userId: user?.id }
      });
    }
  }, [user, signOut]); // REMOVED state.lastActivity to prevent infinite loop

  // Reset timer manually (for "Stay Active" button)
  const resetTimer = useCallback(() => {
    setState(prev => ({ ...prev, isWarning: false }));
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }
    updateActivity();
  }, [updateActivity]);

  // Check for cross-tab activity
  const checkCrossTabActivity = useCallback(() => {
    try {
      const storedActivity = localStorage.getItem(fullConfig.storageKey);
      if (storedActivity) {
        const lastActivity = parseInt(storedActivity, 10);
        const timeSinceActivity = Date.now() - lastActivity;
        
        // If activity in another tab is more recent, reset our timer
        if (timeSinceActivity < state.lastActivity && timeSinceActivity < fullConfig.timeoutMs) {
          setState(prev => ({ ...prev, lastActivity, isWarning: false }));
          if (countdownRef.current) {
            clearInterval(countdownRef.current);
          }
        }
      }
    } catch (error) {
      // Ignore cross-tab check errors
    }
  }, [fullConfig, state.lastActivity]);

  // Setup activity listeners
  useEffect(() => {
    if (!user) {
      isActiveRef.current = false;
      return;
    }

    isActiveRef.current = true;

    // Add activity listeners
    fullConfig.events.forEach(event => {
      document.addEventListener(event, updateActivity, { passive: true });
    });

    // Add storage listener for cross-tab sync
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === fullConfig.storageKey) {
        checkCrossTabActivity();
      }
    };
    window.addEventListener('storage', handleStorageChange);

    // Initialize activity
    updateActivity();

    // Periodic cross-tab check
    const crossTabInterval = setInterval(checkCrossTabActivity, 10000); // Every 10 seconds

    return () => {
      isActiveRef.current = false;
      
      // Remove listeners
      fullConfig.events.forEach(event => {
        document.removeEventListener(event, updateActivity);
      });
      window.removeEventListener('storage', handleStorageChange);
      
      // Clear timers
      if (timerRef.current) clearTimeout(timerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      clearInterval(crossTabInterval);
    };
  }, [user, updateActivity, checkCrossTabActivity, fullConfig]);

  // Handle page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user) {
        // Page became visible - check if we missed the timeout
        const now = Date.now();
        const timeSinceActivity = now - state.lastActivity;
        
        if (timeSinceActivity >= fullConfig.timeoutMs) {
          performAutoLogout();
        } else {
          updateActivity();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user, state.lastActivity, fullConfig.timeoutMs, performAutoLogout, updateActivity]);

  return {
    isWarning: state.isWarning,
    remainingSeconds: state.remainingSeconds,
    resetTimer,
    isActive: !!user && isActiveRef.current
  };
}